import { useProductOptions } from "@/api/services/admin/product";
import { FormField } from "@/components/elements/FormField/FormField";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import { TOrganizationFilterForm } from "@/features/organization/types/organizationFilter";
import { text } from "@/theme/colors";
import { Box, Grid, Typography, styled } from "@mui/material";
import { FC, useEffect } from "react";
import { Control, UseFormTrigger, useFormState, useWatch } from "react-hook-form";

// 16px = theme spacing unit (8px) * 2
const GRID_SPACING = 2;

type DateFieldName = "lastLoginFrom" | "lastLoginTo" | "createdDateFrom" | "createdDateTo";

type Props = {
  control: Control<TOrganizationFilterForm>;
  trigger: UseFormTrigger<TOrganizationFilterForm>;
};

type DateRangeFieldProps = {
  control: Control<TOrganizationFilterForm>;
  trigger: UseFormTrigger<TOrganizationFilterForm>;
  fromName: DateFieldName;
  toName: DateFieldName;
};

const DateRangeField: FC<DateRangeFieldProps> = ({ control, trigger, fromName, toName }) => {
  const fromValue = useWatch({ control, name: fromName });
  const toValue = useWatch({ control, name: toName });
  const { errors } = useFormState({ control });

  // Surface whichever side of the range failed; the message spans both inputs.
  const rangeError = errors[fromName]?.message || errors[toName]?.message;

  // Revalidate both ends whenever either changes so a stale cross-field error clears.
  useEffect(() => {
    trigger([fromName, toName]);
  }, [trigger, fromName, toName, fromValue, toValue]);

  return (
    <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <FormField control={control} name={fromName} hideError noLabel>
          <InputDatePicker maxDate={toValue ?? undefined} />
        </FormField>
      </Box>
      <Typography variant="body14">～</Typography>
      <Box sx={{ flex: 1 }}>
        <FormField control={control} name={toName} hideError noLabel>
          <InputDatePicker minDate={fromValue ?? undefined} />
        </FormField>
      </Box>
      {rangeError && <RangeError>{rangeError}</RangeError>}
    </Box>
  );
};

export const OrganizationFilterTabContent: FC<Props> = ({ control, trigger }) => {
  const { data: products, isLoading } = useProductOptions();

  return (
    <Box px={3} pb={2}>
      <Grid container rowSpacing={GRID_SPACING} columnSpacing={GRID_SPACING}>
        <Grid item xs={6}>
          <FormField control={control} name="externalId">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="name">
            <Input />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField control={control} name="productId">
            <Select options={products ?? []} loading={isLoading} />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField label="最終ログイン">
            <DateRangeField control={control} trigger={trigger} fromName="lastLoginFrom" toName="lastLoginTo" />
          </FormField>
        </Grid>
        <Grid item xs={6}>
          <FormField label="登録日">
            <DateRangeField control={control} trigger={trigger} fromName="createdDateFrom" toName="createdDateTo" />
          </FormField>
        </Grid>
      </Grid>
    </Box>
  );
};

const RangeError = styled((props: { children?: React.ReactNode; className?: string }) => <Typography variant="cap12" component="span" {...props} />)`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  margin-top: 4px;
  color: ${text.error};
`;
